import { injectable, inject } from 'inversify';
import { DI_TYPES } from '../di-types';
import { V2ClientFeatureState } from '../v2/client-state/features';
import { BrowserWindow, getWindowObject as _getWindowObject } from './window-provider';

const nativeFunctionNames = <const>['fetch', 'Request'];

export type NativeFunctions = Partial<Pick<BrowserWindow, typeof nativeFunctionNames[number]>>;

@injectable()
export class NativeFunctionsProvider {
  private window: ReturnType<typeof _getWindowObject> = {};
  private iframeNativeFunctions: Promise<NativeFunctions> | null;
  private clientFeatureState: V2ClientFeatureState;

  constructor(
    @inject(DI_TYPES.WindowProvider) getWindowObject: typeof _getWindowObject,
    @inject(DI_TYPES.V2ClientFeatureState) clientFeatureState: V2ClientFeatureState
  ) {
    this.window = getWindowObject();
    this.clientFeatureState = clientFeatureState;
    if (!this.window?.document || !this.window?.navigator) {
      this.iframeNativeFunctions = null;
    } else {
      this.iframeNativeFunctions = new Promise<NativeFunctions>(resolve =>
        this.onDocReady(() => resolve(this.getNativeFunctionsFromIframe()))
      );
    }
  }

  // based on https://stackoverflow.com/a/9899701
  private onDocReady(fn: () => void) {
    // see if DOM is already available
    if (
      (this.window?.document && this.window?.document.readyState === 'complete') ||
      document.readyState === 'interactive'
    ) {
      // call on next available tick
      setTimeout(fn, 0);
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  private copyFunctionsFromObject = (obj: Partial<BrowserWindow>): NativeFunctions =>
    nativeFunctionNames.reduce<NativeFunctions>((acc, fnName) => {
      const f = obj?.[fnName] as {
        new (...args: any[]): any;
        (...args: any[]): any;
      };
      if (typeof f === 'function') {
        const _self = this;
        acc[fnName] = function (...args: any[]) {
          if (new.target) {
            return new f(...args);
          }

          return f.apply(_self.window, args);
        } as any;
      }
      return acc;
    }, {});

  private getNativeFunctionsFromIframe = (): NativeFunctions => {
    const iframe = this.window?.document?.createElement('iframe');
    if (!iframe) {
      return {};
    }
    iframe.style.display = 'none';
    this.window?.document?.body.appendChild(iframe);
    return this.copyFunctionsFromObject(iframe.contentWindow as BrowserWindow);
  };

  public async getNativeFunctions(): Promise<NativeFunctions> {
    if (this.clientFeatureState.getFeatureState('IFRAME_NATIVES') && this.iframeNativeFunctions) {
      return this.iframeNativeFunctions;
    }

    return this.window;
  }
}
